/* eslint-disable import/prefer-default-export */
import algoliasearch from 'algoliasearch/lite';

export const APP_ID = process.env.GATSBY_ALGOLIA_APP_ID;
export const SEARCH_KEY = process.env.GATSBY_ALGOLIA_SEARCH_KEY;

export const PRODUCTS_INDEX = process.env.GATSBY_ALGOLIA_PRODUCTS_INDEX || 'pilates_us_shopify_products';

//	Products tab shows only physical products (identified by this Shopify tag).
export const PRODUCTS_BASE_FILTER = 'tags:physical_product';

//	Content tab merges these two indices under one "Content" tab.
const CONTENT_INDICES = [
	process.env.GATSBY_ALGOLIA_CONTENT_PAGES_INDEX || 'pilates_us_content_pages',
	process.env.GATSBY_ALGOLIA_BLOG_PAGES_INDEX || 'pilates_us_blog_pages',
];

//	Videos tab index (YouTube videos, each linked to a video/product page).
export const VIDEOS_INDEX = process.env.GATSBY_ALGOLIA_VIDEOS_INDEX || 'pilates_us_videos';

//	Each Sort dropdown value maps to the Algolia index pre-sorted that way:
//	the primary index for Relevance, and a (virtual) sort replica otherwise.
export const PRODUCT_SORT_INDICES = {
	relevance: PRODUCTS_INDEX,
	'title asc': process.env.GATSBY_ALGOLIA_SORT_TITLE_ASC || `${ PRODUCTS_INDEX }_title_asc`, // Name (A-Z)
	'date desc': process.env.GATSBY_ALGOLIA_SORT_DATE_DESC || `${ PRODUCTS_INDEX }_updated_at_desc`, // Newest
	'price asc': process.env.GATSBY_ALGOLIA_SORT_PRICE_ASC || `${ PRODUCTS_INDEX }_price_asc`, // Price: Low to High
	'price desc': process.env.GATSBY_ALGOLIA_SORT_PRICE_DESC || `${ PRODUCTS_INDEX }_price_desc`, // Price: High to Low
};

const client = (typeof window !== 'undefined' && APP_ID && SEARCH_KEY)
	? algoliasearch(APP_ID, SEARCH_KEY)
	: null;

//	Client instance for react-instantsearch (needs an instance during SSR too).
export const searchClient = (APP_ID && SEARCH_KEY)
	? algoliasearch(APP_ID, SEARCH_KEY)
	: { search: () => Promise.resolve({ results: [] }) };

const EMPTY_RESULT = {
	hits: [], nbHits: 0, page: 0, nbPages: 0, facets: {},
};

//	Lightweight query powering the header autosuggest flyout.
export const suggestProducts = ({ query = '', hitsPerPage = 8 } = {}) => {
	if (!client) return Promise.resolve(EMPTY_RESULT);

	return client.initIndex(PRODUCTS_INDEX).search(query, {
		hitsPerPage,
		facetFilters: [PRODUCTS_BASE_FILTER],
		//	`id` (Shopify product id) is needed to match this click back up with
		//	an add-to-cart later; clickAnalytics gets us a queryID back so the
		//	click/conversion can be attributed to this search at all.
		attributesToRetrieve: ['id', 'title', 'handle', 'image', 'product_image', 'product_type', 'price'],
		attributesToHighlight: ['title'],
		clickAnalytics: true,
	});
};

//	Content search across both content indices, merged into one result.
export const searchContent = async({ query = '', page = 0, hitsPerPage = 12 } = {}) => {
	if (!client) {
		return {
			hits: [], nbHits: 0, page: 0, nbPages: 0,
		};
	}

	const { results } = await client.search(
		CONTENT_INDICES.map(indexName => ({ indexName, query, params: { page, hitsPerPage } }))
	);

	const hits = results.reduce((acc, r) => acc.concat(r.hits || []), []);
	const nbHits = results.reduce((sum, r) => sum + (r.nbHits || 0), 0);
	const nbPages = results.reduce((max, r) => Math.max(max, r.nbPages || 0), 0);
	return {
		hits, nbHits, page, nbPages,
	};
};

//	Lightweight content query powering the "Search Suggestions" column of the header
//	flyout (titles only, highlighted), across both content indices.
export const suggestContent = async({ query = '', hitsPerPage = 3 } = {}) => {
	if (!client) return EMPTY_RESULT;

	const { results } = await client.search(
		CONTENT_INDICES.map(indexName => ({
			indexName,
			query,
			params: {
				hitsPerPage,
				attributesToRetrieve: ['title'],
				attributesToHighlight: ['title'],
			},
		}))
	);

	const hits = results.reduce((acc, r) => acc.concat(r.hits || []), []);
	return { ...EMPTY_RESULT, hits };
};

//	Videos tab search (single index, paginated).
export const searchVideos = async({ query = '', page = 0, hitsPerPage = 12 } = {}) => {
	if (!client) {
		return {
			hits: [], nbHits: 0, page: 0, nbPages: 0,
		};
	}

	const {
		hits, nbHits, nbPages,
	} = await client.initIndex(VIDEOS_INDEX).search(query, { page, hitsPerPage });
	return {
		hits, nbHits, page, nbPages,
	};
};

//	Lightweight video query for the "Videos" group of the header flyout's Search Suggestions.
export const suggestVideos = async({ query = '', hitsPerPage = 3 } = {}) => {
	if (!client) return EMPTY_RESULT;

	return client.initIndex(VIDEOS_INDEX).search(query, {
		hitsPerPage,
		attributesToRetrieve: ['title'],
		attributesToHighlight: ['title'],
	});
};
